import { useState } from "react";
import Nav from "@/components/Nav";
import Catalog from "@/components/Catalog";
import Footer from "@/components/Footer";
import BookingDialog from "@/components/BookingDialog";
import AuthModal from "@/components/AuthModal";
import WalletModal from "@/components/WalletModal";
import SalesTicker from "@/components/SalesTicker";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";
import { useAuth } from "@/context/AuthContext";
import { domainCheckout, cryptoDomainCheckout, walletBuy, priceToNumber, formatUSD } from "@/lib/apiClient";
import { toast } from "sonner";

export default function Landing() {
  const { user, refresh } = useAuth();
  const [selected, setSelected] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [walletOpen, setWalletOpen] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [payDomain, setPayDomain] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const onInquire = (domain) => {
    setSelected(domain);
    setBookingOpen(true);
  };

  const onTickerSelect = (domain) => {
    // Priced domains → buy flow; no-price ("On Request") → Inquiry dialog
    if (priceToNumber(domain.price) != null) onBuy(domain);
    else onInquire(domain);
  };

  const startCardCheckout = async (domain) => {
    setPayOpen(false);
    try {
      const { checkout_url } = await domainCheckout({
        domain_id: domain.id, origin_url: window.location.origin, buyer_email: user?.email,
      });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
    }
  };

  const startCryptoCheckout = async (domain) => {
    setPayOpen(false);
    try {
      const { checkout_url } = await cryptoDomainCheckout({
        domain_id: domain.id, origin_url: window.location.origin, buyer_email: user?.email,
      });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Crypto payments are not available right now.");
    }
  };

  const onBuy = async (domain) => {
    const price = priceToNumber(domain.price);
    // Logged-in user with enough balance → instant wallet purchase
    if (user && (user.wallet_balance || 0) >= price) {
      if (!window.confirm(`Buy ${domain.name} for ${formatUSD(price)} from your wallet balance?`)) return;
      try {
        const res = await walletBuy(domain.id);
        toast.success(`${res.domain_name} acquired! New balance ${formatUSD(res.wallet_balance)}.`);
        await refresh();
        setReloadSignal((n) => n + 1);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Purchase failed.");
      }
      return;
    }
    // Otherwise → let the buyer choose card or crypto
    setPayDomain(domain);
    setPayOpen(true);
  };

  return (
    <div className="relative" data-testid="landing-page">
      <Nav onOpenAuth={(mode) => { setAuthMode(mode || "login"); setAuthOpen(true); }} onOpenWallet={() => setWalletOpen(true)} />
      <div className="pt-16 sm:pt-20">
        <SalesTicker onSelect={onTickerSelect} reloadSignal={reloadSignal} />
        <Catalog onInquire={onInquire} onBuy={onBuy} reloadSignal={reloadSignal} />
        <Footer />
      </div>
      <BookingDialog domain={selected} open={bookingOpen} onOpenChange={setBookingOpen} />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
      <WalletModal open={walletOpen} onOpenChange={setWalletOpen} />
      <PaymentMethodDialog
        domain={payDomain}
        open={payOpen}
        onOpenChange={setPayOpen}
        onCard={startCardCheckout}
        onCrypto={startCryptoCheckout}
      />
    </div>
  );
}
